/**
 * ApproverBadge - 審批人標籤組件
 * 
 * 顯示單個審批人信息，支持：
 * 1. 顯示審批人名稱和狀態
 * 2. 編輯/刪除操作
 * 3. 拖拉排序 (串行模式)
 */

import React, { useCallback } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useI18n } from '../../context/I18nContext';

// 狀態圖標
const StatusIcon = ({ status }) => {
    switch (status) {
        case 'completed':
            return (
                <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
            );
        case 'current':
            return (
                <svg className="w-4 h-4 text-blue-600 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
            );
        case 'rejected':
            return (
                <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
            );
        default:
            return (
                <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm0-2a6 6 0 100-12 6 6 0 000 12z" clipRule="evenodd" />
                </svg>
            );
    }
};

/**
 * ApproverBadge - 可拖拉的審批人標籤
 * 
 * @param {Object} props
 * @param {Object} props.approver - 審批人數據
 * @param {number} props.index - 在列表中的索引
 * @param {string} props.status - 審批狀態
 * @param {boolean} props.canEdit - 是否可編輯
 * @param {boolean} props.canRemove - 是否可刪除
 * @param {boolean} props.canDrag - 是否可拖拉
 * @param {Function} props.onEdit - 編輯回調
 * @param {Function} props.onRemove - 刪除回調
 * @param {boolean} props.showOrder - 是否顯示序號
 */
export default function ApproverBadge({
    approver,
    index,
    status = 'pending',
    canEdit = false,
    canRemove = false,
    canDrag = false,
    onEdit,
    onRemove,
    showOrder = false,
    id,
}) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ 
        id: id || approver.userId || approver.email,
        disabled: !canDrag
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const statusStyles = {
        completed: 'bg-green-50 border-green-200 text-green-700',
        current: 'bg-blue-50 border-blue-300 text-blue-700',
        pending: 'bg-gray-50 border-gray-200 text-gray-700',
        rejected: 'bg-red-50 border-red-200 text-red-700',
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${statusStyles[status] || statusStyles.pending} ${isDragging ? 'shadow-lg ring-2 ring-blue-400' : ''}`}
        >
            {/* 拖拉手柄 */}
            {canDrag && (
                <div
                    {...attributes}
                    {...listeners}
                    className="cursor-grab active:cursor-grabbing p-1 text-gray-400 hover:text-gray-600"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                    </svg>
                </div>
            )}

            {/* 序號 */}
            {showOrder && (
                <span className="shrink-0 w-5 h-5 flex items-center justify-center bg-white rounded-full text-xs font-medium border">
                    {index + 1}
                </span>
            )}

            {/* 狀態圖標 */}
            <StatusIcon status={status} />

            {/* 審批人頭像和名稱 */}
            <div className="flex items-center gap-2 flex-1 min-w-0">
                <div className="w-6 h-6 rounded-full bg-white border flex items-center justify-center text-xs font-medium shrink-0">
                    {approver.displayName?.[0] || '?'}
                </div>
                <div className="min-w-0">
                    <div className="text-sm font-medium truncate">
                        {approver.displayName}
                    </div>
                    {approver.department && (
                        <div className="text-xs opacity-75 truncate">
                            {approver.department}
                        </div>
                    )}
                </div>
            </div>

            {/* 操作按鈕 */}
            <div className="flex items-center gap-1">
                {canEdit && (
                    <button
                        onClick={() => onEdit?.(approver)}
                        className="p-1 text-gray-400 hover:text-blue-600 rounded"
                        title="編輯"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                    </button>
                )}
                {canRemove && (
                    <button
                        onClick={() => onRemove?.(approver)}
                        className="p-1 text-gray-400 hover:text-red-600 rounded"
                        title="移除"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                )}
            </div>
        </div>
    );
}

/**
 * ApproverBadgeGroup - 審批人群組 (支持拖拽排序)
 * 
 * @param {Array} props.approvers - 審批人列表
 * @param {string} props.status - 審批狀態
 * @param {string} props.approvalMode - 審批模式
 * @param {boolean} props.canEdit - 是否可編輯
 * @param {Function} props.onEdit - 編輯回調
 * @param {Function} props.onRemove - 刪除回調
 * @param {Function} props.onAdd - 新增回調
 * @param {Function} props.onReorder - 重新排序回調
 */
export function ApproverBadgeGroup({
    approvers,
    status = 'pending',
    approvalMode = 'parallel_any',
    canEdit = false,
    onEdit,
    onRemove,
    onAdd,
    onReorder,
}) {
    const { t } = useI18n(['common', 'forms']);
    
    // 是否啟用拖拽（僅依序審批且可編輯時啟用）
    const canDrag = canEdit && approvalMode === 'sequential' && approvers.length > 1;
    
    const modeLabels = {
        sequential: t('forms:route.modeSequential') || '依序審批',
        parallel_any: t('forms:route.modeParallelAny') || '任一審批',
        parallel_all: t('forms:route.modeParallelAll') || '全部審批',
        parallel_majority: t('forms:route.modeParallelMajority') || '多數審批',
    };

    // DnD 感測器設定
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 5,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // 處理拖拽結束
    const handleDragEnd = useCallback((event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = approvers.findIndex(a => (a.userId || a.email) === active.id);
        const newIndex = approvers.findIndex(a => (a.userId || a.email) === over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
            const newApprovers = arrayMove(approvers, oldIndex, newIndex);
            onReorder?.(newApprovers);
        }
    }, [approvers, onReorder]);

    // 獲取可排序的 ID 列表
    const sortableIds = approvers.map(a => a.userId || a.email);

    const renderApproverList = () => (
        <div className="space-y-1">
            {approvers.map((approver, idx) => (
                <ApproverBadge
                    key={approver.userId || approver.email}
                    id={approver.userId || approver.email}
                    approver={approver}
                    index={idx}
                    status={approver.status || status}
                    canEdit={canEdit}
                    canRemove={canEdit && approvers.length > 1}
                    canDrag={canDrag}
                    showOrder={approvalMode === 'sequential'}
                    onEdit={onEdit}
                    onRemove={onRemove}
                />
            ))}
        </div>
    );

    return (
        <div className="space-y-2">
            {/* 模式標籤 */}
            {approvers.length > 1 && (
                <div className="flex items-center gap-2 text-xs text-gray-500">
                    <span className="px-2 py-0.5 bg-gray-100 rounded">
                        {modeLabels[approvalMode] || approvalMode}
                    </span>
                </div>
            )}
            
            {/* 審批人列表 - 依序模式時啟用 DnD */}
            {canDrag ? (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={sortableIds}
                        strategy={verticalListSortingStrategy}
                    >
                        {renderApproverList()}
                    </SortableContext>
                </DndContext>
            ) : (
                renderApproverList()
            )}

            {/* 新增審批人按鈕 */}
            {canEdit && onAdd && (
                <button
                    onClick={onAdd}
                    className="w-full flex items-center justify-center gap-1 px-3 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-blue-400 hover:text-blue-600"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    {t('forms:route.addApprover') || '新增審批人'}
                </button>
            )}
        </div>
    );
}
